/**
 * useFrameManager -- Frame loading, caching, and navigation for timelapse analysis.
 *
 * Replaces the HTML5 <video> element approach with direct JPEG frame rendering.
 * Frames are fetched as presigned S3 URLs, loaded as ImageBitmap objects, and
 * cached in an LRU window around the current frame index.
 */
import { useState, useEffect, useRef, useCallback } from 'react';

const CACHE_WINDOW = 30; // Max frames to keep in memory (~60MB for 2MP frames)
const PREFETCH_AHEAD = 10; // Prefetch this many frames ahead of current

export default function useFrameManager({ gpuUrl, sessionId, serverReady }) {
  const [frameCount, setFrameCount] = useState(0);
  const [fps, setFps] = useState(5);
  const [frameTimestamps, setFrameTimestamps] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentBitmap, setCurrentBitmap] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 });
  const [framesReady, setFramesReady] = useState(false);
  const [framesError, setFramesError] = useState(null);

  // Internal refs
  const frameUrlsRef = useRef({}); // {index: presignedUrl}
  const bitmapCacheRef = useRef(new Map()); // index -> ImageBitmap
  const loadingRef = useRef(new Set()); // indices currently being fetched
  const playIntervalRef = useRef(null);
  const mountedRef = useRef(true);
  // Track which bitmap is currently rendered so eviction never closes a
  // bitmap React still holds in `currentBitmap` state — closing it triggers
  // "Failed to execute 'drawImage'... source is detached" on the next render.
  const displayedBitmapRef = useRef(null);
  const displayedIndexRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    const cacheRef = bitmapCacheRef.current;
    return () => {
      mountedRef.current = false;
      if (playIntervalRef.current) clearInterval(playIntervalRef.current);
      // Clean up bitmaps
      cacheRef.forEach((bmp) => {
        if (bmp && typeof bmp.close === 'function') bmp.close();
      });
      cacheRef.clear();
    };
  }, []);

  // Fetch frame info + frame URLs when session is ready
  useEffect(() => {
    if (!serverReady || !sessionId || !gpuUrl) return;
    const encoded = encodeURIComponent(sessionId);

    // Fetch frame info (count, fps, timestamps)
    fetch(`${gpuUrl}/frame_info/${sessionId}?sessionId=${encoded}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (!mountedRef.current) return;
        if (data.frame_count) setFrameCount(data.frame_count);
        if (data.fps) setFps(data.fps);
        if (data.frame_timestamps) {
          // Reverse timestamp order to match reversed frame URLs
          // (index 0 = most recent frame, index N-1 = earliest)
          const ts = data.frame_timestamps;
          const tsKeys = Object.keys(ts).map(Number).sort((a, b) => a - b);
          const total = tsKeys.length;
          const reversedTs = {};
          for (let i = 0; i < total; i++) {
            reversedTs[total - 1 - i] = ts[tsKeys[i]];
          }
          setFrameTimestamps(reversedTs);
        }
      })
      .catch((err) => console.error('Failed to fetch frame info:', err));

    // Fetch all frame URLs
    fetch(`${gpuUrl}/frame_urls/${sessionId}?sessionId=${encoded}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (!mountedRef.current) return;
        if (data.frame_urls) {
          // Reverse frame order so index 0 = most recent, index N-1 = earliest.
          // The backend returns {0: oldest, ..., N-1: newest}; we flip so the
          // scrubber and default view show the latest image first.
          const raw = data.frame_urls;
          const keys = Object.keys(raw).map(Number).sort((a, b) => a - b);
          const total = keys.length;
          const reversed = {};
          for (let i = 0; i < total; i++) {
            reversed[String(i)] = raw[String(keys[total - 1 - i])];
          }
          frameUrlsRef.current = reversed;
          if (total && !frameCount) setFrameCount(total);
          // Load the first frame (most recent) immediately and display it
          loadFrame(0).then((bitmap) => {
            if (bitmap && mountedRef.current) {
              displayedBitmapRef.current = bitmap;
              displayedIndexRef.current = 0;
              setCurrentBitmap(bitmap);
              setFramesReady(true);
            } else if (mountedRef.current) {
              setFramesError('First frame could not be decoded. Please refresh.');
            }
          });
        } else {
          // No frame URLs in response
          if (mountedRef.current) setFramesError('No frames found for this experiment. Please refresh.');
        }
      })
      .catch((err) => {
        console.error('Failed to fetch frame URLs:', err);
        if (mountedRef.current) setFramesError('Failed to load experiment frames. Please refresh.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverReady, sessionId, gpuUrl]);

  // Evict frames far from the current position
  const evictCache = useCallback((centerIndex) => {
    const cache = bitmapCacheRef.current;
    if (cache.size <= CACHE_WINDOW) return;

    const entries = [...cache.entries()];
    entries.sort((a, b) => Math.abs(a[0] - centerIndex) - Math.abs(b[0] - centerIndex));
    // Keep closest CACHE_WINDOW frames, evict the rest — but never close the
    // bitmap that's currently being rendered (it lives in component state and
    // would cause an InvalidStateError on the next paint).
    for (let i = CACHE_WINDOW; i < entries.length; i++) {
      const [idx, bmp] = entries[i];
      cache.delete(idx);
      if (
        bmp &&
        typeof bmp.close === 'function' &&
        bmp !== displayedBitmapRef.current &&
        idx !== displayedIndexRef.current
      ) {
        bmp.close();
      }
    }
  }, []);

  // Load a single frame as ImageBitmap
  const loadFrame = useCallback(async (index) => {
    if (bitmapCacheRef.current.has(index)) {
      return bitmapCacheRef.current.get(index);
    }
    const url = frameUrlsRef.current[String(index)];
    if (!url || loadingRef.current.has(index)) return null;

    loadingRef.current.add(index);
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`Frame ${index}: HTTP ${resp.status}`);
      const blob = await resp.blob();
      const bitmap = await createImageBitmap(blob);

      if (!mountedRef.current) {
        bitmap.close();
        return null;
      }

      bitmapCacheRef.current.set(index, bitmap);

      // Set frame dimensions from first loaded frame
      if (!frameSize.width && bitmap.width) {
        setFrameSize({ width: bitmap.width, height: bitmap.height });
      }

      // Evict old frames if cache is too large
      evictCache(index);

      return bitmap;
    } catch (err) {
      console.warn(`Failed to load frame ${index}:`, err.message);
      return null;
    } finally {
      loadingRef.current.delete(index);
    }
  }, [frameSize.width, evictCache]);

  // Prefetch frames around current index
  const prefetch = useCallback(
    (centerIndex) => {
      const total = Object.keys(frameUrlsRef.current).length || frameCount;
      for (let offset = 1; offset <= PREFETCH_AHEAD; offset++) {
        const ahead = centerIndex + offset;
        const behind = centerIndex - offset;
        if (ahead < total && !bitmapCacheRef.current.has(ahead)) loadFrame(ahead);
        if (behind >= 0 && !bitmapCacheRef.current.has(behind)) loadFrame(behind);
      }
    },
    [frameCount, loadFrame],
  );

  // Navigate to a specific frame
  const goToFrame = useCallback(
    async (index) => {
      const clamped = Math.max(0, Math.min(index, frameCount - 1));
      setCurrentIndex(clamped);

      // Load the frame bitmap
      let bitmap = bitmapCacheRef.current.get(clamped);
      if (!bitmap) {
        bitmap = await loadFrame(clamped);
      }
      if (mountedRef.current && bitmap) {
        displayedBitmapRef.current = bitmap;
        displayedIndexRef.current = clamped;
        setCurrentBitmap(bitmap);
      }

      // Prefetch neighbours
      prefetch(clamped);
    },
    [frameCount, loadFrame, prefetch],
  );

  // Play/pause
  const togglePlay = useCallback(() => {
    if (isPlaying) {
      clearInterval(playIntervalRef.current);
      playIntervalRef.current = null;
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
      const intervalMs = Math.max(50, 1000 / fps); // Min 50ms between frames
      playIntervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          const next = prev + 1;
          if (next >= frameCount) {
            clearInterval(playIntervalRef.current);
            playIntervalRef.current = null;
            setIsPlaying(false);
            return prev;
          }
          // Load and display the next frame
          const bitmap = bitmapCacheRef.current.get(next);
          if (bitmap) {
            displayedBitmapRef.current = bitmap;
            displayedIndexRef.current = next;
            setCurrentBitmap(bitmap);
          } else {
            loadFrame(next).then((bmp) => {
              if (bmp) {
                displayedBitmapRef.current = bmp;
                displayedIndexRef.current = next;
                setCurrentBitmap(bmp);
              }
            });
          }
          prefetch(next);
          return next;
        });
      }, intervalMs);
    }
  }, [isPlaying, fps, frameCount, loadFrame, prefetch]);

  // Stop playback on unmount or session change
  useEffect(() => {
    return () => {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
        playIntervalRef.current = null;
      }
    };
  }, [sessionId]);

  // Convert display index (0 = most recent) to backend index (0 = earliest).
  // All backend API calls (add_prompt, propagate, automask, etc.) expect the
  // original chronological frame index.
  const toBackendIndex = useCallback(
    (displayIdx) => frameCount > 0 ? frameCount - 1 - displayIdx : displayIdx,
    [frameCount],
  );

  // Convert backend index to display index (inverse of toBackendIndex).
  const toDisplayIndex = useCallback(
    (backendIdx) => frameCount > 0 ? frameCount - 1 - backendIdx : backendIdx,
    [frameCount],
  );

  return {
    frameCount,
    fps,
    frameTimestamps,
    currentIndex,
    currentBitmap,
    frameSize,
    // Presigned URL of the current frame (for the LocateAnything assistant to fetch).
    currentFrameUrl: frameUrlsRef.current[String(currentIndex)] || null,
    isPlaying,
    framesReady,
    framesError,
    goToFrame,
    togglePlay,
    toBackendIndex,
    toDisplayIndex,
    setCurrentIndex: goToFrame, // alias for compatibility
  };
}
